import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { Company, CompanyIntel, Grade, ScrapeJob } from '@/types';
import { calculateOverallGrade } from '@/utils/helpers';

interface AppState {
  // Auth
  userEmail: string;
  isAuthenticated: boolean;
  isAdmin: boolean;
  adminToken: string;
  timezone: string;
  accessCustomKpi: boolean;
  accessContentScraping: boolean;
  accessProfitPod: boolean;
  accessBusinessIntel: boolean;
  accessPepaiResearch: boolean;
  accessEmailWorkspace: boolean;

  // Credits
  credits: number;
  contactCredits: number;

  // Companies
  companies: Company[];
  filteredCompanies: Company[];

  // Intel data
  recruiterIntelData: Record<string, CompanyIntel>;

  // LAL (Look-A-Like) list
  lalList: Record<string, boolean>;

  // UI state
  loading: boolean;
  currentPage: number;
  perPage: number;
  searchQuery: string;
  gradeFilter: string;

  // Scraping modal
  scrapeModalOpen: boolean;
  scrapeProgress: number;
  scrapeStatus: string;
  scrapeSource: string;
  scrapeCompanyName: string;

  // Data drawer
  drawerOpen: boolean;
  drawerCompanyId: string;
  drawerCompanyName: string;
  drawerSource: string;

  // Grade drawer
  gradeDrawerOpen: boolean;
  gradeDrawerCompanyId: string;
  gradeDrawerCompanyName: string;
  gradeDrawerType: string;

  // Background scrapes
  scrapeJobs: ScrapeJob[];

  // Actions
  setUserEmail: (email: string) => void;
  setAuthenticated: (isAuth: boolean) => void;
  setIsAdmin: (isAdmin: boolean) => void;
  setAdminToken: (token: string) => void;
  setTimezone: (timezone: string) => void;
  setAccessCustomKpi: (enabled: boolean) => void;
  setAccessContentScraping: (enabled: boolean) => void;
  setAccessProfitPod: (enabled: boolean) => void;
  setAccessBusinessIntel: (enabled: boolean) => void;
  setAccessPepaiResearch: (enabled: boolean) => void;
  setAccessEmailWorkspace: (enabled: boolean) => void;
  logout: () => void;

  setCredits: (credits: number) => void;
  setContactCredits: (credits: number) => void;

  setCompanies: (companies: Company[]) => void;
  setFilteredCompanies: (companies: Company[]) => void;

  setRecruiterIntelData: (data: Record<string, CompanyIntel>) => void;
  updateIntelData: (companyId: string, intel: CompanyIntel) => void;

  setLALList: (list: Record<string, boolean>) => void;
  toggleLAL: (companyId: string) => void;

  setLoading: (loading: boolean) => void;
  setCurrentPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setSearchQuery: (query: string) => void;
  setGradeFilter: (filter: string) => void;

  // Scrape modal actions
  openScrapeModal: (source: string, companyName: string) => void;
  updateScrapeProgress: (progress: number, status: string) => void;
  closeScrapeModal: () => void;

  // Data drawer actions
  openDrawer: (companyId: string, companyName: string, source: string) => void;
  closeDrawer: () => void;

  // Grade drawer actions
  openGradeDrawer: (companyId: string, companyName: string, gradeType: string) => void;
  closeGradeDrawer: () => void;

  // Scrape job actions
  addScrapeJob: (job: ScrapeJob) => void;
  updateScrapeJob: (jobId: string, updates: Partial<ScrapeJob>) => void;
  removeScrapeJob: (jobId: string) => void;
  clearCompletedJobs: () => void;

  // Filter companies based on search and grade
  filterCompanies: () => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial state
      userEmail: '',
      isAuthenticated: false,
      isAdmin: false,
      adminToken: '',
      timezone: 'America/Chicago',
      accessCustomKpi: true,
      accessContentScraping: true,
      accessProfitPod: true,
      accessBusinessIntel: true,
      accessPepaiResearch: false,
      accessEmailWorkspace: false,
      credits: 0,
      contactCredits: 0,
      companies: [],
      filteredCompanies: [],
      recruiterIntelData: {},
      lalList: {},
      loading: false,
      currentPage: 1,
      perPage: 50,
      searchQuery: '',
      gradeFilter: '',
      scrapeModalOpen: false,
      scrapeProgress: 0,
      scrapeStatus: '',
      scrapeSource: '',
      scrapeCompanyName: '',
      drawerOpen: false,
      drawerCompanyId: '',
      drawerCompanyName: '',
      drawerSource: '',
      gradeDrawerOpen: false,
      gradeDrawerCompanyId: '',
      gradeDrawerCompanyName: '',
      gradeDrawerType: '',
      scrapeJobs: [],

      // Actions
      setUserEmail: (email) => set({ userEmail: email }),
      setAuthenticated: (isAuth) => set({ isAuthenticated: isAuth }),
      setIsAdmin: (isAdmin) => set({ isAdmin }),
      setAdminToken: (adminToken) => set({ adminToken }),
      setTimezone: (timezone) => set({ timezone }),
      setAccessCustomKpi: (enabled) => set({ accessCustomKpi: enabled }),
      setAccessContentScraping: (enabled) => set({ accessContentScraping: enabled }),
      setAccessProfitPod: (enabled) => set({ accessProfitPod: enabled }),
      setAccessBusinessIntel: (enabled) => set({ accessBusinessIntel: enabled }),
      setAccessPepaiResearch: (enabled) => set({ accessPepaiResearch: enabled }),
      setAccessEmailWorkspace: (enabled) => set({ accessEmailWorkspace: enabled }),
      logout: () =>
        set({
          userEmail: '',
          isAuthenticated: false,
          isAdmin: false,
          adminToken: '',
          timezone: 'America/Chicago',
          credits: 0,
          contactCredits: 0,
          accessCustomKpi: true,
          accessContentScraping: true,
          accessProfitPod: true,
          accessBusinessIntel: true,
          accessPepaiResearch: false,
          accessEmailWorkspace: false,
        }),

      setCredits: (credits) => set({ credits }),
      setContactCredits: (contactCredits) => set({ contactCredits }),

      setCompanies: (companies) => {
        set({ companies });
        get().filterCompanies();
      },

      setFilteredCompanies: (companies) => set({ filteredCompanies: companies }),

      setRecruiterIntelData: (data) => set({ recruiterIntelData: data }),

      updateIntelData: (companyId, intel) =>
        set((state) => ({
          recruiterIntelData: {
            ...state.recruiterIntelData,
            [companyId]: {
              ...state.recruiterIntelData[companyId],
              ...intel,
            },
          },
        })),

      setLALList: (list) => set({ lalList: list }),

      toggleLAL: (companyId) =>
        set((state) => {
          const newList = { ...state.lalList };
          if (newList[companyId]) {
            delete newList[companyId];
          } else {
            newList[companyId] = true;
          }
          return { lalList: newList };
        }),

      setLoading: (loading) => set({ loading }),
      setCurrentPage: (page) => set({ currentPage: page }),
      setPerPage: (perPage) => set({ perPage, currentPage: 1 }),

      setSearchQuery: (query) => {
        set({ searchQuery: query, currentPage: 1 });
        get().filterCompanies();
      },

      setGradeFilter: (filter) => {
        set({ gradeFilter: filter, currentPage: 1 });
        get().filterCompanies();
      },

      // Scrape modal
      openScrapeModal: (source, companyName) =>
        set({
          scrapeModalOpen: true,
          scrapeProgress: 0,
          scrapeStatus: 'Initializing...',
          scrapeSource: source,
          scrapeCompanyName: companyName,
        }),

      updateScrapeProgress: (progress, status) =>
        set({ scrapeProgress: progress, scrapeStatus: status }),

      closeScrapeModal: () =>
        set({
          scrapeModalOpen: false,
          scrapeProgress: 0,
          scrapeStatus: '',
          scrapeSource: '',
          scrapeCompanyName: '',
        }),

      // Data drawer
      openDrawer: (companyId, companyName, source) =>
        set({
          drawerOpen: true,
          drawerCompanyId: companyId,
          drawerCompanyName: companyName,
          drawerSource: source,
        }),

      closeDrawer: () =>
        set({
          drawerOpen: false,
          drawerCompanyId: '',
          drawerCompanyName: '',
          drawerSource: '',
        }),

      // Grade drawer
      openGradeDrawer: (companyId, companyName, gradeType) =>
        set({
          gradeDrawerOpen: true,
          gradeDrawerCompanyId: companyId,
          gradeDrawerCompanyName: companyName,
          gradeDrawerType: gradeType,
        }),

      closeGradeDrawer: () =>
        set({
          gradeDrawerOpen: false,
          gradeDrawerCompanyId: '',
          gradeDrawerCompanyName: '',
          gradeDrawerType: '',
        }),

      addScrapeJob: (job) =>
        set((state) => ({
          scrapeJobs: [job, ...state.scrapeJobs].slice(0, 50),
        })),

      updateScrapeJob: (jobId, updates) =>
        set((state) => ({
          scrapeJobs: state.scrapeJobs.map((job) =>
            job.id === jobId ? { ...job, ...updates } : job
          ),
        })),

      removeScrapeJob: (jobId) =>
        set((state) => ({
          scrapeJobs: state.scrapeJobs.filter((job) => job.id !== jobId),
        })),

      clearCompletedJobs: () =>
        set((state) => ({
          scrapeJobs: state.scrapeJobs.filter((job) => job.status === 'running' || job.status === 'queued'),
        })),

      // Filter companies
      filterCompanies: () => {
        const { companies, searchQuery, gradeFilter, recruiterIntelData } = get();

        let filtered = [...companies];

        // Search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          filtered = filtered.filter((c) =>
            c.companyName.toLowerCase().includes(query)
          );
        }

        // Grade filter
        if (gradeFilter) {
          filtered = filtered.filter((c) => {
            const intel = recruiterIntelData[c.companyId];
            const grade = calculateOverallGrade(intel) as Grade | null;
            return grade === gradeFilter;
          });
        }

        set({ filteredCompanies: filtered });
      },
    }),
    {
      name: 'profitibull-storage',
      storage: createJSONStorage(() => ({
        getItem: (name: string) => localStorage.getItem(name),
        setItem: (name: string, value: string) => {
          try {
            localStorage.setItem(name, value);
          } catch {
            // If storage is full, drop persisted app state instead of crashing the UI.
            try {
              localStorage.removeItem(name);
              localStorage.setItem(name, value);
            } catch {
              // Ignore persistence failure.
            }
          }
        },
        removeItem: (name: string) => localStorage.removeItem(name),
      })),
      partialize: (state) => ({
        userEmail: state.userEmail,
        isAuthenticated: state.isAuthenticated,
        isAdmin: state.isAdmin,
        accessCustomKpi: state.accessCustomKpi,
        accessContentScraping: state.accessContentScraping,
        accessProfitPod: state.accessProfitPod,
        accessBusinessIntel: state.accessBusinessIntel,
        accessPepaiResearch: state.accessPepaiResearch,
        accessEmailWorkspace: state.accessEmailWorkspace,
        perPage: state.perPage,
        // Keep persisted state small to avoid localStorage quota issues.
        lalList: Object.fromEntries(Object.entries(state.lalList).slice(0, 300)),
      }),
    }
  )
);
